// Frontend view of the canonical analytics event registry.
// Source of truth: analytics/events.schema.json (imported directly; the
// Python mirror is generated - see analytics/generate_python_registry.py).
// Validation semantics must match python-functions/functions/analytics/
// event_ingest.py: unknown events/fields, missing required fields, type or
// enum violations and over-long strings are all rejected.

import registry from '../../analytics/events.schema.json';

export const REGISTRY_VERSION = registry.registryVersion;
export const EVENTS = registry.events;
export const PRODUCTS = registry.products.values;
const DOMAIN_DEFAULT_PRODUCTS = registry.products.domainDefaults;

// Product is a dimension, not part of the name: a surface belonging to a
// different product than its event family (HFP riding RMS form machinery)
// passes an explicit product and nothing needs renaming if it splits out.
// Server-side resolution mirrors this (event_ingest._resolve_product).
export function resolveProduct(eventName, requested) {
  if (PRODUCTS.includes(requested)) return requested;
  return DOMAIN_DEFAULT_PRODUCTS[EVENTS[eventName]?.domain];
}

// Exported so that call sites which truncate their own text (rather than
// letting a long value fail validation) cap it at the registry's limit instead
// of re-typing the number.
export const MAX_STRING_LEN = 128;
const TYPE_RE =
  /^(string|id|bool|int|float|ts|enum\(([^()<>]+)\)|list<(string|id|int|enum\(([^()<>]+)\))>)(\?)?$/;

const splitEnum = (spec) => spec.split(',').map((v) => v.trim());

const checkValue = (value, base, enumValues) => {
  switch (base) {
    case 'bool':
      return typeof value === 'boolean';
    case 'int':
      return Number.isInteger(value);
    case 'float':
      return typeof value === 'number' && Number.isFinite(value);
    case 'string':
    case 'id':
      return typeof value === 'string' && value.length <= MAX_STRING_LEN;
    case 'ts':
      return (
        typeof value === 'string' &&
        value.length <= MAX_STRING_LEN &&
        !Number.isNaN(Date.parse(value))
      );
    case 'enum':
      return typeof value === 'string' && enumValues.includes(value);
    default:
      return false;
  }
};

const checkTyped = (value, typeSpec) => {
  const match = TYPE_RE.exec(typeSpec);
  if (!match) return false;
  const full = match[1];
  if (full.startsWith('list<')) {
    if (!Array.isArray(value)) return false;
    const inner = match[3];
    if (inner.startsWith('enum(')) {
      return value.every((v) => checkValue(v, 'enum', splitEnum(match[4])));
    }
    return value.every((v) => checkValue(v, inner, null));
  }
  if (full.startsWith('enum(')) {
    return checkValue(value, 'enum', splitEnum(match[2]));
  }
  return checkValue(value, full, null);
};

// Returns a list of validation errors; empty when the event is valid.
export function validateEventPayload(eventName, payload = {}) {
  const definition = EVENTS[eventName];
  if (!definition) return [`unknown event: ${eventName}`];
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) {
    return ['payload must be an object'];
  }

  const errors = [];
  const fields = definition.payload;
  Object.keys(payload).forEach((key) => {
    if (!(key in fields)) errors.push(`unregistered payload field: ${key}`);
  });
  Object.entries(fields).forEach(([key, typeSpec]) => {
    const optional = typeSpec.endsWith('?');
    const value = payload[key];
    if (value === undefined || value === null) {
      if (!optional) errors.push(`missing required field: ${key}`);
      return;
    }
    if (!checkTyped(value, typeSpec)) {
      errors.push(`invalid value for ${key} (expected ${typeSpec})`);
    }
  });
  return errors;
}
